import { ItemKey, SCOPE_LEVELS } from '@cyberscore/contracts';
import { ChevronRight, Plus, Sparkles, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href, navigate } from '../../lib/router';
import { DataView } from '../../ui/DataView';
import { AiDraft } from './AiDraft';
import { useCreateItem, useStudioAccess } from './api';
import type { ItemSummary } from './journey';
import { newQuestion, newRule } from './editors';
import { SCOPE_LABEL, when } from './StudioPage';

const SPECS: Record<'question' | 'rule', ListSpec> = {
  question: { id: 'questions', sort: 'key', dir: 'asc', filters: ['scope', 'retired'] },
  rule: { id: 'rules', sort: 'key', dir: 'asc', filters: ['scope', 'retired'] },
};

const KINDS = {
  question: {
    title: 'Questions',
    intro: 'What a journey asks: wording, answer type, options and what each answer sets off.',
    open: href.question,
    blank: newQuestion,
    placeholder: 'Q-BACKUP-OFFSITE',
  },
  rule: {
    title: 'Rules',
    intro: 'When certain answers or facts hold, what happens: findings, tasks, reviews, workflows.',
    open: href.rule,
    blank: () => newRule(),
    placeholder: 'R-NO-OFFSITE-BACKUP',
  },
} as const;

/** Questions or rules in the studio (task 43), each opening in its editor; searched and paged on the server (task 60). */
export function ItemsPage({ kind }: { kind: keyof typeof KINDS }) {
  const k = KINDS[kind];
  const access = useStudioAccess();
  const list = useListState(SPECS[kind]);
  const items = usePage<ItemSummary & { title: string }>('/config/items/page', list.state, `kind=${kind}`);
  const [creating, setCreating] = useState(false);
  const [drafting, setDrafting] = useState(false);

  return (
    <>
      <div className="crumbs">
        <a href={href.studio()}>Studio</a>
        <ChevronRight size={14} />
        {k.title}
      </div>
      <div className="page-head row-between">
        <div>
          <h1>{k.title}</h1>
          <p>{k.intro}</p>
        </div>
        {access.edits && (
          <div className="row gap">
            <button type="button" className="btn" onClick={() => setDrafting(true)}>
              <Sparkles size={16} /> Draft with AI
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus size={16} /> New {kind}
            </button>
          </div>
        )}
      </div>
      {drafting && <AiDraft kind={kind} open={k.open} onClose={() => setDrafting(false)} />}
      {creating && <NewItem kind={kind} onClose={() => setCreating(false)} />}

      <DataView
        list={list}
        query={items}
        noun={`${kind}s`}
        rowKey={(i) => i.id}
        rowHref={(i) => k.open(i.id)}
        columns={[
          { key: 'key', label: 'Key', sort: 'key', render: (i) => <a className="mono small" href={k.open(i.id)}>{i.key}</a> },
          { key: 'title', label: kind === 'question' ? 'Question' : 'Name', sort: 'title', render: (i) => <span className="cell-title">{i.title}</span> },
          { key: 'scope', label: 'Scope', render: (i) => <span className="pill neutral">{SCOPE_LABEL[i.scope_level] ?? i.scope_level}</span> },
          { key: 'revision', label: 'Revision', align: 'right', wide: true, render: (i) => <span className="num">{i.revision}</span> },
          { key: 'updated', label: 'Updated', sort: 'updated', wide: true, render: (i) => <span className="muted">{when(i.updated_at)}</span> },
        ]}
        filters={[
          { key: 'scope', label: 'Scopes', options: SCOPE_LEVELS.map((l) => [l, SCOPE_LABEL[l] ?? l] as const) },
          { key: 'retired', label: 'Current or retired', options: [['no', 'Current'], ['yes', 'Retired']] },
        ]}
        sorts={[
          { value: 'key', label: 'Key', dir: 'asc' },
          { value: 'title', label: kind === 'question' ? 'Wording' : 'Name', dir: 'asc' },
          { value: 'updated', label: 'Last updated', dir: 'desc' },
        ]}
        searchLabel="Search by key or wording"
        empty={<>{`No ${kind}s yet.`}</>}
      />
    </>
  ); // prettier-ignore
}

function NewItem({ kind, onClose }: { kind: keyof typeof KINDS; onClose: () => void }) {
  const k = KINDS[kind];
  const create = useCreateItem();
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = ItemKey.safeParse(key);
    if (!parsed.success) return setError(`Key: ${parsed.error.issues[0]!.message}`);
    try {
      const row = await create.mutateAsync({ kind, key, body: k.blank() });
      navigate(k.open(row.id));
    } catch (err) {
      setError(err instanceof ApiError ? (err.issues[0]?.message ?? err.message) : 'Could not create it');
    }
  };
  return (
    <form className="card form-card mb" onSubmit={submit} noValidate>
      <div className="card-head">
        <div className="card-title">New {kind}</div>
        <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <div className="form-body">
        <div className="field">
          <label htmlFor="ni-key">Key</label>
          <input id="ni-key" className="input mono" autoFocus value={key} onChange={(e) => setKey(e.target.value)} placeholder={k.placeholder} />
          <span className="hint">Letters, digits, _ . - ; can’t be changed later. You write the rest in the editor.</span>
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
      </div>
      <div className="form-foot">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          Create {kind}
        </button>
      </div>
    </form>
  ); // prettier-ignore
}
